import { MoneyFormatter } from "../../MoneyFormatter";
import { IReadOnlyResourceManager, IWriteOnlyResourceManager, ResourceManager } from "../../ResourceManager/ResourceManager";
import { CreateElement, LogGroup, Rect, Size } from "../../MyHelpers";
import { MyCanvas } from "../MyCanvas";
import {
    IRewardOptionConfig,
    RewardColor,
    RewardOptionParams,
} from "./RewardOptionFactory";
import { IRewardOption } from "./ScatterRandomAward";
import { DisplayAmount, Settings } from "../../Settings";
import { CreditAwardOption, SlotGameBet } from "../../api";
import * as PIXI from 'pixi.js'
import { ca } from "date-fns/locale";
import { element } from "svelte/internal";
import { service } from "../../services";
import { Spine } from "pixi-spine";


interface TextCache
{
    key: string;
    canvas: MyCanvas;
    valid: boolean
}

interface TextCachePIXI
{
    key: string;
    valueCanvas: PIXI.Text;
    shadowCanvas: PIXI.Text;
    jpCanvas: PIXI.Sprite;
    valid: boolean
}


// represents single cash reward, depends on bet
export class CreditRewardOption implements IRewardOption {
    protected logGroup: LogGroup;

    // protected betCents: number = 0;
    protected valueCredits: number = 0;
    protected strValueToDisplay: string = "\u00D71";
    protected strCashValue: string = "";

    // protected color:string;
    // protected coloroutherStroke:string;
    // protected colorinnerStroke:string;

    protected color: RewardColor;

    protected readonly credits: number = 1;
    protected readonly madeForCredits: number = 1;
    protected readonly config: CreditAwardOption;

    protected readonly creditForCurrentBet: number = 1;

    public constructor(
        params: RewardOptionParams,
        config: CreditAwardOption,
        color: RewardColor
    ) {
        this.credits = config.Credits;
        this.madeForCredits = config.MadeForBetCredits;

        if (!this.madeForCredits)
        {
            throw new Error(`MadeForCredit is not set for credit reward ${this.credits}: ${this.madeForCredits}`);
        }

        this.creditForCurrentBet = this.credits;

        this.logGroup = params.logGroup.Add("Cash").Add(this.credits.toString());
        this.config = config;
        this.color = color;
    }

    public Name(): string {
        return this.config.AwardName;
    }

    public async PrepareResources(resourceManager: IWriteOnlyResourceManager): Promise<void> { }

    public async ResourcesLoaded(resourceManager: IReadOnlyResourceManager): Promise<void> { }

    public UpdateBet(bet: SlotGameBet ): void 
    {
        const multiplier = Math.round(bet.TotalBetCredits / this.madeForCredits);
        if (multiplier < 1)
        {
            throw new Error(`Bet multiplier ${multiplier} is less than 1, SRA is defined as ${this.credits} credits at bet ${this.madeForCredits} credits, current bet is ${bet.TotalBetCredits} credits`);
        }

        this.valueCredits = this.credits * multiplier;

        const valueMoney = service.moneyFormatter.CreditToCentsWithDenom(this.valueCredits, bet.TotalBetCents);
        this.strCashValue = service.moneyFormatter.FormatCurrency(valueMoney);

        if (Settings.DisplayAmount == DisplayAmount.AsCredits) 
        {
            this.strValueToDisplay = this.valueCredits.toString();
        }
        else if (Settings.DisplayAmount == DisplayAmount.AsMoney)
        {
            this.strValueToDisplay = this.strCashValue;             
        }

        this.cachedTextList?.forEach(c => c.valid = false);
        this.cachedTextPIXIList?.forEach(c => c.valid = false);
    }

    
    //protected cachedText: MyCanvas = null;
    //protected cachedTextValid: boolean = false;

    protected cachedTextList: TextCache[] = [];
    protected cachedTextPIXIList: TextCachePIXI[] = [];
    public DrawTo(canvas: MyCanvas): void {
        // TODO create cache (another private canvas) to write text once, then draw the cache
        // drawing text is expensive, save() and restore() is also expensive,
        // drawing image (cache) is not so much

        //let center = canvas.rect.Center();



        // use 2/3 width of sprite to draw text on, to prevent drawing over the sprite border
        // then reduce font size further if displayed string length is longer (more numbers - smaller font size)

        let fontSize = Math.round(
            ((canvas.rect.width / 2.2) * 2) / Math.max(4, this.strValueToDisplay.length)
        );


        let key: string = `sra-cash-cache-${this.valueCredits}-${fontSize}-${canvas.rect.width}-${canvas.rect.height}`;
        let cachedText = this.cachedTextList.find(c => c.key == key);

        // make cache
        if (!cachedText)
        {
            cachedText = {
                key: key,
                canvas: new MyCanvas(canvas.size, {
                    classes: [`sra-cash-cache-${this.valueCredits}`]
                }),
                valid: false
            };

            this.cachedTextList.push(cachedText);

            // need atleast 4 different sizes, 
            // BigSymbolSpinAndHold = reel, holding feature, big symbol reel, big symbol holding feature
            while (this.cachedTextList.length > 4)
            {
                this.cachedTextList[0].canvas.Destroy();
                this.cachedTextList.splice(0, 1);
            }
        }

        if (!cachedText.valid || cachedText.canvas.WasContextLost())
        {
            cachedText.canvas.Clear();
            cachedText.canvas.context.fillStyle = this.color.color;
            cachedText.canvas.context.font = `bold ${fontSize}px NotoSansDisplay-Bold`;
            cachedText.canvas.context.textAlign = "center";

            // fix centering font vertically, depends on fond
            let measurement = cachedText.canvas.context.measureText(this.strValueToDisplay);

            // perfect center
            let yoffset = (measurement.actualBoundingBoxAscent - measurement.actualBoundingBoxDescent) / 2;

            cachedText.canvas.context.shadowBlur = 20;
            cachedText.canvas.context.shadowColor = this.color.coloroutherStroke;
            cachedText.canvas.context.shadowOffsetX = 1;
            cachedText.canvas.context.shadowOffsetY = 1;

            cachedText.canvas.context.strokeStyle = this.color.coloroutherStroke;
            cachedText.canvas.context.lineWidth = fontSize * 0.15;
            cachedText.canvas.context.strokeText(
                this.strValueToDisplay,
                canvas.rect.centerx,
                canvas.rect.centery + yoffset,
                canvas.rect.width
            );

            cachedText.canvas.context.strokeStyle = this.color.colorinnerStroke;
            cachedText.canvas.context.lineWidth = fontSize * 0.1;
            cachedText.canvas.context.strokeText(
                this.strValueToDisplay,
                canvas.rect.centerx,
                canvas.rect.centery + yoffset,
                canvas.rect.width
            );

            cachedText.canvas.context.fillText(
                this.strValueToDisplay,
                canvas.rect.centerx,
                canvas.rect.centery + yoffset,
                canvas.rect.width
            );

            cachedText.valid = true;
            cachedText.canvas.RestoreContext();
        }

        canvas.context.drawImage(
            cachedText.canvas.Canvas(),
            0, 0, cachedText.canvas.size.Width, cachedText.canvas.size.Height,
            0, 0, canvas.size.Width, canvas.size.Height
        );

        key = null;
    }

    //draw sra info to pixi
    public DrawToPIXI(canvas: PIXI.AnimatedSprite, symbolsSize: Size , strValueMultiplier : string) {
        let fontSize = Math.round(
            ((canvas.width / 2) * 2) / Math.max(4,  strValueMultiplier != undefined ? strValueMultiplier.length :  this.strValueToDisplay.length)
        );

        let valueCanvas = canvas.parent.getChildByName("SRAvalue", true) as PIXI.Text;
        let shadowCanvas = canvas.parent.getChildByName("SRAshadowText", true) as PIXI.Text;
        let jpCanvas = canvas.parent.getChildByName("SRAjackpot", true) as PIXI.Text;

        shadowCanvas.style.fontFamily = `NotoSansDisplay-Bold`;
        shadowCanvas.style.align = "center";
        shadowCanvas.text = strValueMultiplier != undefined ? strValueMultiplier : this.strValueToDisplay;
        shadowCanvas.style.fontSize = fontSize;
        shadowCanvas.style.fill = this.color.coloroutherStroke;
        shadowCanvas.style.stroke = this.color.coloroutherStroke;
        shadowCanvas.style.strokeThickness = fontSize / 7,
        shadowCanvas.style.lineHeight = fontSize * 0.1;
        shadowCanvas.style.fontWeight = 'bold';
        shadowCanvas.style.lineJoin = 'round';
        shadowCanvas.anchor.set(0.5, 0.5);

        //create sytle for cache            
        valueCanvas.style.fontFamily = `NotoSansDisplay-Bold`;
        valueCanvas.style.align = "center";
        valueCanvas.text = strValueMultiplier != undefined ? strValueMultiplier : this.strValueToDisplay;
        valueCanvas.style.fontSize = fontSize;
        valueCanvas.style.fill = this.color.color;
        valueCanvas.style.stroke = this.color.colorinnerStroke;
        valueCanvas.style.strokeThickness = fontSize / 8,
        valueCanvas.style.lineHeight = fontSize * 0.1;
        valueCanvas.style.fontWeight = 'bold';
        valueCanvas.style.lineJoin = 'round';
        valueCanvas.anchor.set(0.5, 0.5);

        //value SRA
        valueCanvas.text = valueCanvas.text;
        valueCanvas.visible = true;

        //shadow SRA
        shadowCanvas.text = shadowCanvas.text;
        shadowCanvas.visible = true;

        //Hide canvasJP
        //hide SRAjackpot 
        //show cash and shadow container        
        canvas.parent.children.forEach(element => {
            if (element.name == "SRAjackpot") element.visible = false;
            if (element.name == "SRAshadowText") element.visible = true;
            if (element.name == "SRAvalue") element.visible = true;
        })

        //console.log("Scatter-Size-Sprite:",fontSize);
    }

    
     
    public TextToDraw(): string
    {
        return `CASH ${this.config.AwardName} = ${this.strValueToDisplay}`;
    }

    public Update(): void
    {
    }

    public Cleanup(): void
    {
    }

    protected strValueMultiplier : string = "";
    public MultiplierAward(bonusMultiplier: number) {
        
        let newMultiplier = this.valueCredits * bonusMultiplier;
        if (Settings.DisplayAmount == DisplayAmount.AsCredits) {
            
            this.strValueMultiplier = newMultiplier.toString();
        }
        else if (Settings.DisplayAmount == DisplayAmount.AsMoney) {
            //this.betCents = betCents;
            let betMoney = service.moneyFormatter.FormatCurrency((newMultiplier));
            this.strValueMultiplier = betMoney;
        }
        
        return this.strValueMultiplier
    }

    //protected wasCreated = false ; 
    public DrawToSpine(spine: Spine, symbolsSize?: Size, strValueMultiplier?: string, jackpotMultiplier?: number, awardShow? : boolean) {

        let fontSize = Math.round(
            ((600 / 2 ) * 2) / Math.max(4, strValueMultiplier != null ? strValueMultiplier.length : this.strValueToDisplay.length)
        ); 

        


        
       


        //let containersChildersIntoSpineJP4 = spine.children as PIXI.Container[];


        
        let valueContainer = spine?.slotContainers.find(container => container.name == "value");

        
        if (valueContainer != null) {
            if (awardShow == false)
                valueContainer.children.forEach(ele => {
                    ele.removeFromParent();
                    ele.destroy(false)
                });

            let containerAward = new PIXI.Container();
            containerAward.name = "containerAward";
            valueContainer.addChild(containerAward);

              //create container for SRA value 				
            let shadowCanvas = new PIXI.Text('');
            shadowCanvas.scale.set(1 - 1);
            //shadowCanvas.x = Math.round((spine.width) / 2);
            //shadowCanvas.y = Math.round(spine.height / 2);
            shadowCanvas.name = "SRAshadowCanvas";
            containerAward.addChild(shadowCanvas);
  
            //create container for SRA value 				
            let valueCanvas = new PIXI.Text('');
            valueCanvas.scale.set(1, -1);
            //valueCanvas.x = Math.round((spine.width) / 2);
            //valueCanvas.y = Math.round(spine.height / 2);
            valueCanvas.name = "SRAvalueCanvas";
            containerAward.addChild(valueCanvas);

          

            shadowCanvas.style.fontFamily = `NotoSansDisplay-Bold`;
            shadowCanvas.style.align = "center";
            shadowCanvas.text = strValueMultiplier != undefined ? strValueMultiplier : this.strValueToDisplay;
            shadowCanvas.style.fontSize = fontSize;
            shadowCanvas.style.fill = this.color.coloroutherStroke;
            shadowCanvas.style.stroke = this.color.coloroutherStroke;
            shadowCanvas.style.strokeThickness = fontSize / 7,
            shadowCanvas.style.lineHeight = fontSize * 0.1;
            shadowCanvas.style.fontWeight = 'bold';
            shadowCanvas.style.lineJoin = 'round';
            shadowCanvas.anchor.set(0.5, 0.5);
            //create sytle for cache            
            valueCanvas.style.fontFamily = `NotoSansDisplay-Bold`;
            valueCanvas.style.align = "center";
            valueCanvas.text = strValueMultiplier != null ? strValueMultiplier : this.strValueToDisplay;
            valueCanvas.style.fontSize = fontSize;
            valueCanvas.style.fill = this.color.color;
            valueCanvas.style.stroke = this.color.colorinnerStroke;
            valueCanvas.style.strokeThickness = fontSize / 8,
            valueCanvas.style.lineHeight = fontSize * 0.1;
            valueCanvas.style.fontWeight = 'bold';
            valueCanvas.style.lineJoin = 'round';
            valueCanvas.anchor.set(0.5, 0.5);

            //value SRA
            valueCanvas.text = valueCanvas.text;
            valueCanvas.visible = true;

            //shadow SRA
            shadowCanvas.text = shadowCanvas.text;
            shadowCanvas.visible = true;  
            
            //console.log("Scatter-Size-Spine:",fontSize);
        }
    }
}

